import React, { useEffect, useMemo, useState, useRef } from 'react'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { GetServerSideProps } from 'next'
import { Constants } from '@/assets'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import { Routes } from '@/assets/configs'
import useEMTState from '@/store/useEMTState'
import { useIsQueryOwner, useLogout } from '@/hooks'
import NextImage from 'next/image'
import { SEO, withPrivateRoute, withPrivateRouteProps } from '@/components'
import { Navigation } from '@/components/EMT/Navigation'
import Footer from '@/components/EMT/Footer'
import { NotificationCardsView } from '@/components/EMT/NotificationCardsView'
import NavNextLink from '@/components/EMT/NavNextLink'
import { HomeBottomCard, ProfilMenu } from '@/components/Home'
import NextALink from '@/components/NextALink'
// import NewServicePetSitterModal from '@/components/PetSitter/NewServicePetSitterModal'
import ProfileMenuPets from '@/components/EMT/ProfileMenuPets'
import {
  EmtAvatar,
  EmtButton,
  EmtCard,
  EmtChip,
  EmtGridContainer,
  EmtHeader,
  EmtIcon,
  EmtMenuBackground,
  EmtModal,
  EmtNavLink,
  EmtTypography,
  useMobile,
} from 'emt-components-v2'

import PetsittersSelection from '@/components/PetSitter/PetsittersSelection/PetsittersSelection'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Container from '@mui/material/Container'
import VariantImages from '@/assets/VariantImages'
import BecomePremiumButton from '@/components/EMT/BecomePremiumButton'
import LinkButton from '@/components/EMT/LinkButton'
import BottomCard from '@/components/Home/BottomCard'
import { styled } from '@mui/material/styles'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import { useQuery } from '@tanstack/react-query'
import { useEMTDispatch } from '@/store'
import { UserServiceHelper } from '@/modules/User'
import { DateTime } from 'luxon'
import ProfileCardV2 from '@/components/EMT/ProfileCardV2'
import { IsThereAvatarBanner } from '@/components/IsThereAvatarBanner'
import { NewPetSitters } from '@/components/Home/NewPetSitters'
import useCurrentUser from '@/hooks/useCurrentUser'
import { SearchServiceHelper } from '@/modules/Search'
import { SearchType } from '@/modules/Search/apiTypes/base'
import FloatingProfileModal from '@/components/FloatingProfileModal'
// import { KycVerification } from '@/helpers/KYCVerificationHelper'

import AppModeSelect from '@/components/EMT/AppModeSelect'

// Types
import { getTrialDuration } from '@/data'
import { Benefices } from '@/components/PetSitter/Profile'

import { useNewRequestCartStore } from '@/store/newRequestCartStore'

const MenuLine = styled(EmtNavLink)(({ theme }) => ({
  // Override EmtNavLink flexDirection
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
  },
}))

type Props = withPrivateRouteProps & EMTServerSideProps

const HomePage: React.FC<Props> = ({ url, title }) => {
  const { isPetsittingMode } = usePetsittingMode()
  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const [activeHomeProfilMenu, setActiveHomeProfilMenu] = useState<boolean>(false)
  const [isMobile] = useMobile()
  const router = useRouter()
  const [isQueryOwner, queryType, appendQueryType] = useIsQueryOwner()
  const [reactivationModalIsActive, setReactivationModalIsActive] = useState<boolean>(false)
  const petsittersSelectionRef = useRef<HTMLDivElement>(null)

  const {
    user: { reactivation, currentPrices },
    discussions: { unreadCount: unreadMessagesCount },
    apiNotifications: { currentApiNotifications },
  } = useEMTState()

  const favoritesCount = currentApiNotifications.filter(
    (apiNotification) => apiNotification.status === 'unread' && apiNotification.context === 'favorite',
  ).length

  const dispatch = useEMTDispatch()

  const trialDurationDays = getTrialDuration(currentPrices || null)

  const { isPremium, isCertified, avatar, settings, currentUser, tendersEnabled } = useCurrentUser()
  const pets = currentUser?.animals || []
  const { insurance } = settings?.affiliationStatus || {}

  const totalCards =
    (isCertified ? 0 : 1) + (isPremium ? 0 : 1) + (unreadMessagesCount ? 1 : 0) + (insurance ? 1 : 0) + favoritesCount

  const { onLogoutClickAsync } = useLogout()

  // [HOOK] Floating Mobile Profile
  const floating = useFloatingProfile({
    defaultProfileKind: ProfileKind.Community,
  })

  const { t } = useTranslation('home')
  const tc = useTranslation('common').t

  const showSmsFeature = useMemo(() => {
    const currentDate = DateTime.fromISO(new Date().toISOString())
    const endDate = DateTime.fromISO(new Date(2023, 4, 20).toISOString())

    return !(currentDate.startOf('day') > endDate.startOf('day'))
  }, [])

  // 🔥Get new profiles
  const { isLoading, data: matchingCards } = useQuery(
    [`home_profiles`],
    async () => {
      const fetchedData = await SearchServiceHelper.searchProfilesAsync(isQueryOwner, {
        userAddress: settings?.address,
        type: SearchType.createdAt,
      })
      return fetchedData.member
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )

  useEffect(() => {
    if (!reactivation) {
      return
    }

    setReactivationModalIsActive(true)
    UserServiceHelper.dispatchGetMyInfoAsync(dispatch)({ reactivation: false })
  }, [reactivation])

  return (
    <>
      <SEO url={url} subtitle={title} />
      <Box sx={{ overflow: 'hidden' }}>
        <Navigation />
      </Box>

      <EmtHeader background="/theme/header-pattern.png" backgroundColor={isPetsittingMode ? '#762D8A' : '#E01B3F'}>
        <IsThereAvatarBanner showHeader={false} sx={{ paddingBottom: '20px' }} t={tc} />
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: !!totalCards ? '20px' : '20px' }}>
          {/* Mobile profile */}
          {isMobile && (
            <>
              {router.pathname === Routes.Home &&
                isQueryOwner &&
                (!isPetsittingMode || Constants.node_env !== 'production') && (
                  <Box sx={{ width: '100%', maxWidth: '100%' }}>
                    <AppModeSelect appendQueryType={appendQueryType} />
                  </Box>
                )}
              <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    maxWidth: isMobile ? '122px' : 150,
                    objectFit: 'contain',
                    '& img': { maxHeight: '50px', objectFit: 'contain', width: '100%' },
                  }}
                >
                  <NextImage
                    src={isPetsittingMode ? VariantImages.LogoSitterWhite : VariantImages.Logo}
                    quality={100}
                    alt={''}
                  />
                </Box>
                <Box id="home-profil" sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      ...(isQueryOwner && { marginRight: '5px' }),
                    }}
                    onClick={() => setActiveHomeProfilMenu(true)}
                  >
                    {isQueryOwner ? (
                      <>
                        <EmtAvatar
                          variant="rounded"
                          src={pets?.[0]?.mainPictureThumbnailUrl}
                          sx={{
                            zIndex: 1,
                            border: isPetsittingMode ? '2px solid purple' : '2px solid #E11B3F',
                          }}
                        />
                        <EmtAvatar
                          id="profil-avatar"
                          variant="rounded"
                          sx={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            transform: 'translate(5px, -5px)',
                            zIndex: 0,
                            backgroundColor: isPetsittingMode ? 'purple.main' : '#B71538',
                          }}
                        />
                      </>
                    ) : (
                      <EmtAvatar
                        variant="rounded"
                        src={avatar?.thumbnailUrl}
                        sx={{
                          zIndex: 1,
                          border: '2px solid #E11B3F',
                        }}
                      />
                    )}
                  </Box>
                  <ProfilMenu
                    anchorEl={document.getElementById('home-profil')}
                    open={activeHomeProfilMenu}
                    onClose={() => setActiveHomeProfilMenu(false)}
                    anchorOrigin={{ horizontal: 'right', vertical: 55 }}
                    transitionDuration={50}
                    MenuListProps={{
                      'aria-labelledby': 'basic-button',
                    }}
                    sx={{
                      '& p': { fontWeight: '700 !important' },
                    }}
                  >
                    {isQueryOwner ? (
                      <ProfileMenuPets pets={currentUser?.animals} />
                    ) : (
                      <>
                        <NavNextLink
                          key="edit-profile"
                          href={appendQueryType(Routes.MyProfile)}
                          sx={{
                            flexDirection: 'row !important',
                          }}
                        >
                          <EmtIcon className="emt-edit-1" sx={{ fontWeight: 'bold' }} />
                          <EmtTypography variant="body2" fontWeight="bold">
                            {tc('navigation.editProfile')}
                          </EmtTypography>
                        </NavNextLink>

                        <MenuLine onClick={onLogoutClickAsync}>
                          <EmtIcon className="emt-logout" sx={{ fontWeight: 'bold' }} />
                          <EmtTypography variant="body2" fontWeight="bold">
                            {tc('navigation.logout')}
                          </EmtTypography>
                        </MenuLine>
                      </>
                    )}
                  </ProfilMenu>
                </Box>
              </Box>
            </>
          )}
          {/* END Mobile profile */}

          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
              justifyContent: isMobile ? 'initial' : 'space-between',
            }}
          >
            {!isMobile && (
              <NextALink href={appendQueryType(Routes.Home)}>
                <Box
                  sx={{
                    maxWidth: '122px',
                    objectFit: 'contain',
                    '& img': { maxHeight: '50px', objectFit: 'contain', width: '100%' },
                  }}
                >
                  <NextImage
                    src={isPetsittingMode ? VariantImages.LogoSitterWhite : VariantImages.Logo}
                    quality={100}
                    alt={'Site logo'}
                  />
                </Box>
              </NextALink>
            )}
            <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: '5px',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'flex-start' : 'flex-end',
                }}
              >
                <EmtTypography variant="h5" color="white" fontWeight="normal">
                  <Trans t={t} i18nKey="header.hello" values={{ name: currentUser?.displayName }} />
                </EmtTypography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                  {isPremium && (
                    <EmtChip
                      label={tc('profileCard.premium') as string}
                      color="secondarylight"
                      size="medium"
                      icon={<EmtIcon className="emt-crown" />}
                    />
                  )}
                  {isCertified && (
                    <EmtChip label={tc('profileCard.idChecked') as string} color="successlight" size="medium" />
                  )}
                </Box>
              </Box>
              <EmtTypography variant="body2" color="white">
                {isQueryOwner ? t('header.upToDateOwner') : t('header.upToDateBorrower')}
              </EmtTypography>
            </Box>
          </Box>

          <NotificationCardsView
            totalCards={totalCards}
            isPremium={isPremium}
            isCertified={isCertified}
            petSitterAllowed={Constants.brand === 'emt' && isQueryOwner && isPetsittingMode}
            favoritesCount={favoritesCount}
            trialDurationDays={trialDurationDays}
            unreadMessagesCount={unreadMessagesCount || 0}
            insurance={insurance}
            settings={settings}
            showSmsFeature={showSmsFeature}
            queryType={queryType}
            appendQueryType={appendQueryType}
          />
        </Box>
      </EmtHeader>

      {/* TO DO: remove */}
      {/* <KycVerification userId={currentUser?.id || ''} /> */}

      <Container
        sx={{
          paddingTop: '30px',
          paddingBottom: isMobile ? '0px' : '60px',
          display: 'flex',
          flexDirection: 'column',
          rowGap: '30px',
        }}
        maxWidth="lg"
      >
        {!isPetsittingMode && (
          <>
            {isQueryOwner ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <EmtTypography variant="h4" fontWeight={800}>
                  {t('content.titleNewBorrowers')}
                </EmtTypography>
                <EmtTypography variant="body1">{t('content.textNewBorrowers')}</EmtTypography>
              </Box>
            ) : (
              <EmtTypography variant="h4" fontWeight={800}>
                {t('content.title')}
              </EmtTypography>
            )}

            {/* CARDS */}
            {!isMobile && (
              <EmtGridContainer>
                {isLoading
                  ? [...Array(8)].map((_, k) => (
                      <Grid key={k} item xs={6} md={3}>
                        <EmtCard loader />
                      </Grid>
                    ))
                  : matchingCards?.map((matchingCard, k) => (
                      <Grid key={k} item xs={6} md={3}>
                        <ProfileCardV2 key={k} profile={matchingCard} sx={{ minWidth: '180px' }} />
                      </Grid>
                    ))}
              </EmtGridContainer>
            )}

            {isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  overflow: 'auto',
                  gap: '15px',
                  '&::-webkit-scrollbar': { display: 'none' },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                {isLoading ? (
                  <EmtGridContainer>
                    {[...Array(2)].map((_, k) => (
                      <Grid key={k} item xs={6} md={3}>
                        <EmtCard loader />
                      </Grid>
                    ))}
                  </EmtGridContainer>
                ) : (
                  matchingCards?.map((matchingCard, k) => (
                    <ProfileCardV2
                      key={k}
                      profile={matchingCard}
                      onMobileClickCard={floating.handleOpenProfile}
                      sx={{ minWidth: '180px' }}
                    />
                  ))
                )}
              </Box>
            )}
            {/* END CARDS */}

            {/* Show more button */}
            <Box sx={{ textAlign: 'center' }}>
              <LinkButton
                href={appendQueryType(Routes.Search._pathname)}
                fullWidth={isMobile}
                startIcon={<EmtIcon className="emt-search-1" />}
              >
                {t(`content.showAllButton.${queryType}`)}
              </LinkButton>
            </Box>
            {/* END Show more button */}
          </>
        )}

        {isMobile && isPetsittingMode && (
          <PetsittersSelection
            ref={petsittersSelectionRef}
            sx={{
              display: petsittersIds.length === 0 ? 'none' : 'block',
              position: 'relative',
              top: '0',
              zIndex: 998,
            }}
            reactNativeWebviewClassName={false}
            openFloatingProfile={floating.handleOpenProfile}
          />
        )}

        {/* Pet sitters */}
        {isQueryOwner && Constants.brand === 'emt' && isPetsittingMode && !tendersEnabled && (
          <NewPetSitters handleOpenProfile={floating.handleOpenProfile} />
        )}
        {/* END Pet sitters */}

        {/* {isQueryOwner && Constants.brand === 'emt' && ( isPetsittingMode) && (
          <NewServicePetSitterModal />
        )} */}
        {/* Info Card */}
        {!isQueryOwner && (
          <HomeBottomCard elevation={0} className="premium">
            {isMobile && (
              <EmtTypography variant="h5">
                {isQueryOwner ? t('content.infoOwner.title') : t('content.infoBorrower.title')}
              </EmtTypography>
            )}
            {!isMobile && <Box>ℹ️</Box>}
            <Box className="emtPaper-home-card-innerContent">
              {!isMobile && (
                <EmtTypography variant="h5">
                  {isQueryOwner ? t('content.infoOwner.title') : t('content.infoBorrower.title')}
                </EmtTypography>
              )}
              <EmtTypography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {isQueryOwner ? t('content.infoOwner.description') : t('content.infoBorrower.description')}
              </EmtTypography>
            </Box>
          </HomeBottomCard>
        )}
        {/* END Info Card */}

        {/* Premium Card */}
        {!isPremium && (
          <HomeBottomCard elevation={0} className="premium">
            {isMobile && (
              <EmtTypography variant="h5">
                {isPetsittingMode
                  ? t(`content.premium.title.dogownerPetsitting`)
                  : t(`content.premium.title.${queryType}`)}
              </EmtTypography>
            )}

            <Box>
              <NextImage
                src={VariantImages.HomePremium}
                quality={100}
                width={isMobile ? 280 : 200}
                alt={''}
                style={{ objectFit: 'contain' }}
              />
            </Box>
            <Box className="emtPaper-home-card-innerContent">
              {!isMobile && (
                <EmtTypography variant="h5">
                  {isPetsittingMode
                    ? t(`content.premium.title.dogownerPetsitting`)
                    : t(`content.premium.title.${queryType}`)}
                </EmtTypography>
              )}
              <EmtTypography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {isPetsittingMode
                  ? t(`content.premium.description.dogownerPetsitting`)
                  : t(`content.premium.description.${queryType}`)}
              </EmtTypography>
              <BecomePremiumButton
                sx={{ minWidth: isMobile ? 'unset' : '172px!important' }}
                fullWidth={isMobile}
                size={isMobile ? 'large' : 'medium'}
              />
            </Box>
          </HomeBottomCard>
        )}
        {/* END Premium Card */}

        {/* Safety Card */}
        <HomeBottomCard elevation={0} className="premium">
          {isMobile && <EmtTypography variant="h5">{t(`content.safety.title`)}</EmtTypography>}

          <Box sx={{ flexShrink: '0' }}>
            <NextImage
              src={VariantImages.HomeSafety}
              quality={100}
              width={isMobile ? 280 : 200}
              alt={''}
              style={{ objectFit: 'contain' }}
            />
          </Box>
          <Box className="emtPaper-home-card-innerContent">
            {!isMobile && <EmtTypography variant="h5">{t(`content.safety.title`)}</EmtTypography>}
            <EmtTypography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {isPetsittingMode ? t(`content.safety.descriptionPetsitting`) : t(`content.safety.description`)}
            </EmtTypography>
            <LinkButton
              href={appendQueryType(Routes.Safety)}
              sx={{ minWidth: isMobile ? 'unset' : '172px!important' }}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              {t('content.safety.link')}
            </LinkButton>
          </Box>
        </HomeBottomCard>
        {/* END Safety Card */}

        {/* Bottom Cards */}
        {!isPetsittingMode ? (
          <>
            <EmtTypography variant="h4" fontWeight={800}>
              {t('content.tips.title')}
            </EmtTypography>
            <EmtGridContainer>
              <BottomCard
                image={VariantImages.HomeGoodConduct}
                title={t('content.tips.goodConduct.title')}
                button={t('content.tips.learnMore')}
                href={t('content.tips.goodConduct.link')}
              />
              <BottomCard
                image={VariantImages.HomeMessagesTips}
                title={t('content.tips.messagesTips.title')}
                button={t('content.tips.learnMore')}
                href={t(`content.tips.messagesTips.link.${queryType}`)}
              />
              <BottomCard
                image={VariantImages.HomeProfileTips}
                title={t('content.tips.profileTips.title')}
                button={t('content.tips.learnMore')}
                href={t(`content.tips.profileTips.link.${queryType}`)}
              />
              {isQueryOwner && (
                <BottomCard
                  image={VariantImages.HomeDogForm}
                  title={t('content.tips.dogForm.title')}
                  button={t('content.tips.downloadForm')}
                  href={t(`content.tips.dogForm.link`)}
                />
              )}
              {isQueryOwner && (
                <BottomCard
                  image={VariantImages.HomeDogLeash}
                  title={t('content.tips.dogLeash.title')}
                  button={t('content.tips.downloadForm')}
                  href={t(`content.tips.dogLeash.link`)}
                />
              )}
            </EmtGridContainer>
          </>
        ) : (
          <>
            {/* Benefices */}
            <Benefices />
            {/* END Benefices */}
          </>
        )}
        {/* END Bottom Cards */}
      </Container>

      {!isMobile && (
        <PetsittersSelection
          ref={petsittersSelectionRef}
          sx={{ position: 'sticky', display: petsittersIds.length === 0 ? 'none' : 'block' }}
        />
      )}

      {activeHomeProfilMenu && isMobile && <EmtMenuBackground />}

      {/* MOBILE PROFILE MODAL */}
      <FloatingProfileModal
        isMobile={floating.isMobile}
        open={floating.profileIsOpen}
        customerId={floating.customerId}
        profileKind={floating.profileKind}
        onClose={floating.handleCloseProfile}
      />
      {/* END MOBILE PROFILE MODAL */}

      <Footer />

      {/* Can't delete account Modal */}
      <EmtModal
        open={reactivationModalIsActive}
        onClose={() => setReactivationModalIsActive(false)}
        disableScrollLock
        sx={{
          '& .emt-icons': {
            fontSize: 14,
            color: (theme: any) => theme.palette.greyGradient[550],
            top: 20,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            textAlign: 'center',
          }}
        >
          <NextImage src={VariantImages.AccountReactivate} quality={100} alt={''} />
          <EmtTypography variant="h4" fontWeight={800}>
            {t('reactivateModal.title')}
          </EmtTypography>
          <div>
            <EmtTypography variant="h5">{t('reactivateModal.subtitle')}</EmtTypography>
            <EmtTypography variant="body1">{t('reactivateModal.text')}</EmtTypography>
          </div>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: '15px',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
            }}
          >
            <EmtButton onClick={() => setReactivationModalIsActive(false)} sx={{ paddingY: '10px' }}>
              {t('reactivateModal.button')}
            </EmtButton>
          </Box>
        </Box>
      </EmtModal>
      {/* END Can't delete account Modal */}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = getEMTServerSideProps({
  namespaces: ['common', 'home', 'profile', 'message', 'petsitter', 'connection'],
  i18nTitleNs: 'home',
  i18nTitleKey: 'title',
})

export default withPrivateRoute()(HomePage)
